import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { getArenaSocket } from './arenaSocket';
import { buildScout, fmtK } from './scouting';

/* ════════ The Taco Stand — lobby (The Window) + matchmaking + invite ════════ */
const styles = `
  .ts-page { min-height: calc(100vh - 60px); background: #FFFFFF;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00;
    --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B; --surface:#F5F5F7; --surface2:#EBEBED;
    --green:#34C759; --green-soft:rgba(52,199,89,.14); --green-ink:#1B873F; --hair:#EBEBED;
    --cream:#FFF8F1; --paper:#FFFDF8; --paper-edge:#EFE4D2; --mono:'SF Mono',ui-monospace,'Menlo',monospace;
    --ease:cubic-bezier(0.16,1,0.3,1); }
  .ts-page * { box-sizing: border-box; }

  /* awning */
  .ts-awning { height:14px; background:repeating-linear-gradient(90deg,var(--brand) 0 34px,#FFE7CC 34px 68px); position:relative; }
  .ts-awning::after { content:''; position:absolute; left:0; right:0; bottom:-11px; height:12px;
    background:radial-gradient(circle 11px at 17px 0,var(--brand) 0 10px,transparent 11px) 0 0/34px 12px repeat-x; }

  /* open sign */
  .ts-open { display:inline-flex; align-items:center; gap:8px; font-size:11px; font-weight:700; letter-spacing:.12em;
    text-transform:uppercase; color:var(--brand-ink); background:var(--brand-soft); padding:6px 12px; border-radius:980px; }
  .ts-open .pip { width:7px; height:7px; border-radius:50%; background:var(--brand); animation:tspip 2.2s infinite; }
  .ts-open.green { color:var(--green-ink); background:var(--green-soft); }
  .ts-open.green .pip { background:var(--green); }
  @keyframes tspip { 0%,100%{opacity:1} 50%{opacity:.35} }

  /* hero */
  .ts-hero { position:relative; background:linear-gradient(180deg,var(--cream),#fff 80%); border-bottom:1px solid var(--hair); }
  .ts-hero-in { max-width:1180px; margin:0 auto; padding:42px 40px 38px; display:flex; align-items:center; gap:48px; }
  .ts-hero-left { flex:1; min-width:0; }
  .ts-eyebrow { display:inline-flex; align-items:center; gap:9px; font-size:11px; font-weight:700; letter-spacing:.16em;
    text-transform:uppercase; color:var(--brand-ink); margin-bottom:18px; }
  .ts-eyebrow .l { width:22px; height:1.5px; background:var(--brand); opacity:.5; }
  .ts-headline { font-size:58px; font-weight:700; letter-spacing:-.045em; line-height:.98; margin-bottom:18px; color:var(--ink); }
  .ts-headline .q { color:var(--ink3); }
  .ts-dek { font-size:18px; color:var(--ink2); line-height:1.5; max-width:440px; margin-bottom:8px; }
  .ts-fine { font-size:13px; color:var(--ink3); margin-bottom:26px; }

  /* setup */
  .ts-setup { display:flex; flex-direction:column; gap:14px; max-width:460px; }
  .ts-seg { display:inline-flex; background:rgba(0,0,0,.05); border-radius:11px; padding:4px; gap:2px; width:fit-content; }
  .ts-seg button { padding:8px 18px; border-radius:8px; font-size:13px; font-weight:500; color:var(--ink2); border:none; background:none; cursor:pointer; font-family:inherit; }
  .ts-seg button.on { background:#fff; color:var(--ink); box-shadow:0 1px 3px rgba(0,0,0,.08); }
  .ts-chips { display:flex; flex-wrap:wrap; gap:8px; }
  .ts-chip { padding:7px 14px; border-radius:980px; border:1.5px solid #D2D2D7; font-size:13px; color:var(--ink2);
    background:#fff; cursor:pointer; font-family:inherit; transition:all .12s; white-space:nowrap; }
  .ts-chip.on { background:var(--ink); border-color:var(--ink); color:#fff; }
  .ts-chip:hover:not(.on) { background:var(--surface); }
  .ts-chip:active { transform:scale(.95); }
  .ts-cta-row { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .ts-btn-step { display:inline-flex; align-items:center; gap:10px; background:var(--ink); color:#fff; padding:16px 32px;
    border-radius:980px; font-size:16px; font-weight:600; letter-spacing:-.01em; border:none; cursor:pointer; font-family:inherit; transition:all .25s var(--ease); }
  .ts-btn-step:hover { transform:translateY(-2px); opacity:.9; }
  .ts-btn-step:active:not(:disabled) { transform:scale(.97); opacity:.9; }
  .ts-btn-step:disabled { opacity:.4; cursor:default; transform:none; }
  .ts-btn-invite { display:inline-flex; align-items:center; gap:8px; background:#fff; border:1px solid var(--hair);
    color:var(--ink); padding:14px 22px; border-radius:980px; font-size:14px; font-weight:500; cursor:pointer; font-family:inherit; }
  .ts-btn-invite:hover { background:var(--surface); }
  .ts-cta-note { font-size:13px; color:var(--ink3); }

  /* gate (logged out) */
  .ts-gate { display:flex; flex-direction:column; gap:10px; max-width:460px; }
  .ts-gate-form { display:grid; grid-template-columns:1fr 1fr auto; gap:8px; }
  .ts-gate-in { min-width:0; background:#fff; border:1px solid var(--hair); border-radius:12px; font-size:15px; font-family:inherit;
    padding:14px 16px; color:var(--ink); outline:none; }
  .ts-gate-in:focus { box-shadow:0 0 0 3px rgba(0,0,0,.06); }
  .ts-gate-sub { white-space:nowrap; padding:14px 26px; background:var(--ink); color:#fff; border:none; border-radius:980px;
    font-size:15px; font-weight:600; cursor:pointer; font-family:inherit; }
  .ts-gate-sub:disabled { opacity:.4; cursor:default; }
  .ts-gate-sent { font-size:14px; color:var(--green-ink); font-weight:500; }
  .ts-gate-err { font-size:13px; color:#FF3B30; }

  /* the window visual */
  .ts-window { flex:none; width:300px; }
  .ts-window-frame { background:#fff; border:1px solid var(--hair); border-radius:20px; box-shadow:0 8px 24px rgba(0,0,0,.08); overflow:hidden; }
  .ts-window-top { height:10px; background:repeating-linear-gradient(90deg,var(--brand) 0 26px,#FFE7CC 26px 52px); }
  .ts-window-body { padding:26px 24px 22px; text-align:center; }
  .ts-host { width:96px; height:96px; border-radius:50%; background:radial-gradient(circle at 50% 38%,#FFD9A8,#FFB55C);
    margin:0 auto 14px; display:flex; align-items:center; justify-content:center; font-size:52px; position:relative; box-shadow:inset 0 -6px 14px rgba(180,90,0,.18); }
  .ts-host .steam { position:absolute; top:-16px; left:50%; transform:translateX(-50%); display:flex; gap:7px; }
  .ts-host .steam i { width:3px; height:18px; border-radius:3px; background:rgba(255,122,0,.28); animation:tssteam 2.6s ease-in-out infinite; }
  .ts-host .steam i:nth-child(2){ animation-delay:.5s; height:24px; }
  .ts-host .steam i:nth-child(3){ animation-delay:1s; }
  @keyframes tssteam { 0%{opacity:0;transform:translateY(4px) scaleY(.7)} 40%{opacity:.9} 100%{opacity:0;transform:translateY(-10px) scaleY(1.1)} }
  .ts-host-says { font-size:18px; font-weight:600; letter-spacing:-.01em; margin-bottom:4px; color:var(--ink); }
  .ts-host-sub { font-size:13px; color:var(--ink3); }
  .ts-window-foot { border-top:1px dashed var(--paper-edge); padding:14px 24px; display:flex; align-items:center; justify-content:space-between; background:var(--paper); }
  .ts-wf-num { font-family:var(--mono); font-size:13px; color:var(--ink2); }
  .ts-wf-num b { color:var(--ink); }

  /* content */
  .ts-content { max-width:1180px; margin:0 auto; padding:28px 40px 70px; display:grid; grid-template-columns:1fr 336px; gap:36px; align-items:start; }
  .ts-section { margin-bottom:34px; }
  .ts-sechead { display:flex; align-items:center; gap:10px; margin-bottom:14px; }
  .ts-k { font-size:11px; font-weight:600; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); }

  /* punch card */
  .ts-punch { background:var(--paper); border:1px solid var(--paper-edge); border-radius:18px; padding:22px 24px; position:relative; overflow:hidden; }
  .ts-punch::before { content:''; position:absolute; left:0; top:0; bottom:0; width:6px; background:repeating-linear-gradient(180deg,var(--brand) 0 12px,#FFE7CC 12px 24px); }
  .ts-punch-top { display:flex; align-items:flex-end; justify-content:space-between; gap:20px; margin-bottom:18px; padding-left:8px; }
  .ts-punch-title { font-size:19px; font-weight:600; letter-spacing:-.01em; color:var(--ink); }
  .ts-punch-sub { font-size:13px; color:var(--ink3); margin-top:3px; }
  .ts-streak { text-align:right; flex:none; }
  .ts-streak-n { font-size:34px; font-weight:700; letter-spacing:-.03em; display:flex; align-items:center; gap:7px; justify-content:flex-end; color:var(--ink); }
  .ts-streak-l { font-size:11px; color:var(--ink3); letter-spacing:.04em; text-transform:uppercase; }
  .ts-stamps { display:flex; gap:10px; padding-left:8px; margin-bottom:8px; flex-wrap:wrap; }
  .ts-stamp { width:44px; height:44px; border-radius:50%; border:2px dashed var(--paper-edge); display:flex; align-items:center; justify-content:center; font-size:21px; color:#D2D2D7; }
  .ts-stamp.done { border-style:solid; border-color:var(--brand); background:var(--brand-soft); color:var(--brand); }
  .ts-stamp.latest { box-shadow:0 0 0 1px var(--brand), 0 0 14px rgba(255,122,0,.35); }
  .ts-stamp.goal { border-style:solid; border-color:var(--brand); background:#fff; color:var(--brand-ink); font-size:13px; font-weight:700; }
  .ts-punch-foot { padding-left:8px; font-size:13px; color:var(--ink2); margin-top:10px; }
  .ts-punch-foot b { color:var(--brand-ink); }
  .ts-lock { display:inline-flex; align-items:center; gap:6px; background:var(--surface); border-radius:980px; padding:5px 11px; font-size:12px; font-weight:600; color:var(--ink2); margin-left:6px; }

  /* live tickets — ambient, not clickable */
  .ts-rail-tk { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:14px; }
  .ts-lt { background:var(--paper); border:1px solid var(--paper-edge); border-radius:14px; overflow:hidden; transition:transform .25s var(--ease), box-shadow .25s var(--ease); }
  .ts-lt:hover { transform:translateY(-3px); box-shadow:0 10px 24px rgba(120,80,20,.12); }
  .ts-lt-head { display:flex; align-items:center; gap:8px; padding:11px 14px; border-bottom:1px dashed var(--paper-edge); }
  .ts-lt-num { font-family:var(--mono); font-size:11px; color:var(--ink3); }
  .ts-lt-live { margin-left:auto; display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; color:var(--brand-ink); }
  .ts-lt-live .pip { width:6px; height:6px; border-radius:50%; background:var(--brand); animation:tspip 2.2s infinite; }
  .ts-lt-body { padding:13px 14px; }
  .ts-lt-players { display:flex; align-items:center; gap:9px; }
  .ts-lt-avs { display:flex; }
  .ts-lt-av { width:28px; height:28px; border-radius:50%; background:var(--ink); border:2px solid var(--paper); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }
  .ts-lt-av:not(:first-child){ margin-left:-7px; background:var(--ink2); }
  .ts-lt-vs { font-size:13px; color:var(--ink2); }
  .ts-lt-vs b { color:var(--ink); font-weight:600; }
  .ts-lt-foot { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-top:1px dashed var(--paper-edge); font-size:12px; color:var(--ink3); }
  .ts-empty { font-size:14px; color:var(--ink3); padding:18px 0; }

  /* stand card (right) */
  .ts-standcard { background:var(--ink); color:#fff; border-radius:18px; padding:20px; overflow:hidden; }
  .ts-standcard .awn { height:8px; margin:-20px -20px 16px; background:repeating-linear-gradient(90deg,var(--brand) 0 22px,#FFE7CC 22px 44px); }
  .ts-sc-name { font-size:17px; font-weight:600; }
  .ts-sc-handle { font-size:12px; color:rgba(255,255,255,.5); margin-bottom:16px; }
  .ts-sc-stat { display:flex; align-items:baseline; justify-content:space-between; padding:9px 0; border-bottom:1px solid rgba(255,255,255,.1); font-size:13px; color:rgba(255,255,255,.6); }
  .ts-sc-stat:last-child { border:none; }
  .ts-sc-stat b { font-size:15px; color:#fff; font-weight:600; }
  .ts-sc-stat .flame { color:var(--brand); }
  .ts-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:6px; }
  .ts-bdg { display:inline-flex; align-items:center; gap:6px; background:var(--surface); border-radius:980px; padding:6px 11px; font-size:12px; font-weight:500; color:var(--ink2); }
  .ts-bdg.locked { opacity:.45; }

  /* matchmaking + invite overlays */
  .ts-scrim { position:fixed; inset:0; background:rgba(28,21,12,.5); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px;
    --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00;
    --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B; --surface:#F5F5F7; --surface2:#EBEBED;
    --green:#34C759; --green-soft:rgba(52,199,89,.14); --green-ink:#1B873F; --hair:#EBEBED;
    --cream:#FFF8F1; --paper:#FFFDF8; --paper-edge:#EFE4D2; --mono:'SF Mono',ui-monospace,'Menlo',monospace; --ease:cubic-bezier(0.16,1,0.3,1); }
  .ts-mm { width:460px; max-width:94vw; background:#fff; border-radius:24px; overflow:hidden; box-shadow:0 24px 60px rgba(0,0,0,.25); text-align:center; animation:tsmmin .35s var(--ease) both; }
  @keyframes tsmmin { from { opacity:0; transform:translateY(14px) scale(.97); } to { opacity:1; transform:none; } }
  .ts-mm-awn { height:14px; background:repeating-linear-gradient(90deg,var(--brand) 0 30px,#FFE7CC 30px 60px); }
  .ts-mm-body { padding:38px 36px 32px; }
  .ts-mm-host { width:118px; height:118px; border-radius:50%; background:radial-gradient(circle at 50% 38%,#FFD9A8,#FFB55C); margin:0 auto 20px; display:flex; align-items:center; justify-content:center; font-size:62px; position:relative; animation:tsbob 3s ease-in-out infinite; }
  @keyframes tsbob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  .ts-mm-host .steam { position:absolute; top:-20px; left:50%; transform:translateX(-50%); display:flex; gap:8px; }
  .ts-mm-host .steam i { width:3px; height:22px; border-radius:3px; background:rgba(255,122,0,.3); animation:tssteam 2.6s ease-in-out infinite; }
  .ts-mm-host .steam i:nth-child(2){ animation-delay:.5s; height:28px; }
  .ts-mm-host .steam i:nth-child(3){ animation-delay:1s; }
  .ts-mm-title { font-size:24px; font-weight:700; letter-spacing:-.02em; margin-bottom:6px; color:var(--ink); }
  .ts-mm-sub { font-size:14px; color:var(--ink2); margin-bottom:20px; }
  .ts-mm-dots { display:flex; gap:7px; justify-content:center; margin-bottom:20px; }
  .ts-mm-dots i { width:9px; height:9px; border-radius:50%; background:var(--brand); animation:tswait 1.3s ease-in-out infinite; }
  .ts-mm-dots i:nth-child(2){ animation-delay:.18s; }
  .ts-mm-dots i:nth-child(3){ animation-delay:.36s; }
  @keyframes tswait { 0%,100%{opacity:.25;transform:scale(.8)} 50%{opacity:1;transform:scale(1.15)} }
  .ts-mm-meta { font-size:12px; color:var(--ink3); margin-bottom:20px; }
  .ts-btn-leave { background:var(--surface); color:var(--ink2); padding:11px 24px; border-radius:980px; font-size:14px; font-weight:500; border:none; cursor:pointer; font-family:inherit; transition:background .15s, transform .15s var(--ease); }
  .ts-btn-leave:hover { background:var(--surface2); }
  .ts-btn-leave:active { transform:scale(.96); }

  /* found reveal */
  .ts-reveal-eyebrow { font-size:11px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; color:var(--brand-ink); margin-bottom:10px; }
  .ts-reveal-title { font-size:26px; font-weight:700; letter-spacing:-.02em; margin-bottom:4px; color:var(--ink); }
  .ts-reveal-sub { font-size:14px; color:var(--ink2); margin-bottom:22px; }
  .ts-vs { display:flex; align-items:center; justify-content:center; gap:14px; margin-bottom:22px; }
  .ts-vs-card { flex:1; max-width:150px; background:var(--paper); border:1px solid var(--paper-edge); border-radius:16px; padding:16px 12px; }
  .ts-vs-av { width:54px; height:54px; border-radius:50%; background:var(--ink); color:#fff; display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:700; margin:0 auto 10px; overflow:hidden; }
  .ts-vs-av img { width:100%; height:100%; object-fit:cover; }
  .ts-vs-name { font-size:15px; font-weight:600; color:var(--ink); }
  .ts-vs-meta { font-size:12px; color:var(--ink3); margin-top:3px; }
  .ts-vs-mid { flex:none; text-align:center; }
  .ts-vs-mid .lbl { font-size:10px; font-weight:700; letter-spacing:.1em; color:var(--ink3); }
  .ts-vs-mid .count { font-size:40px; font-weight:800; letter-spacing:-.04em; color:var(--brand); line-height:1; }

  /* play the taco button */
  .ts-btn-taco { display:inline-flex; align-items:center; gap:8px; background:var(--brand-soft); border:1px solid transparent;
    color:var(--brand-ink); padding:14px 22px; border-radius:980px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .15s, transform .15s var(--ease); }
  .ts-btn-taco:hover { background:#FFE0C2; }
  .ts-btn-taco:active { transform:scale(.97); }

  /* tale of the tape — dark head-to-head during the prep window */
  .ts-mm { max-height:88dvh; overflow-y:auto; }
  .ts-mm.dark { background:#1D1D1F; color:#fff; }
  .ts-tape-eyebrow { text-align:center; font-size:10px; font-weight:800; letter-spacing:.3em; color:var(--brand); margin-bottom:16px; }
  .ts-tape-head { display:grid; grid-template-columns:1fr 54px 1fr; align-items:start; margin-bottom:6px; }
  .ts-tc { text-align:center; min-width:0; }
  .ts-tc-ava { width:62px; height:62px; border-radius:50%; margin:0 auto 8px; overflow:hidden; background:#333; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:20px; color:#fff; }
  .ts-tc-ava img { width:100%; height:100%; object-fit:cover; }
  .ts-tc-ava.taco { background:radial-gradient(circle at 50% 38%,#FFD9A8,#FFB55C); font-size:30px; }
  .ts-tc.me .ts-tc-ava { box-shadow:0 0 0 3px var(--brand); }
  .ts-tc.op .ts-tc-ava { box-shadow:0 0 0 3px rgba(255,255,255,.35); }
  .ts-tc-name { font-size:14px; font-weight:800; letter-spacing:-.01em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ts-tc-win { font-size:9px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; margin-top:4px; }
  .ts-tc.me .ts-tc-win { color:var(--brand); }
  .ts-tc.op .ts-tc-win { color:#9BE7AE; }
  .ts-tc-fact { font-size:10.5px; color:rgba(255,255,255,.55); margin-top:4px; line-height:1.45; }
  .ts-tape-vs { text-align:center; font-size:19px; font-weight:900; letter-spacing:.04em; color:rgba(255,255,255,.35); padding-top:18px; }
  .ts-tape { margin:16px 0 4px; text-align:left; }
  .ts-tr { display:grid; grid-template-columns:1fr 44px 1fr; align-items:center; gap:8px; margin-bottom:9px; }
  .ts-tb { height:9px; border-radius:99px; background:rgba(255,255,255,.09); position:relative; overflow:hidden; }
  .ts-tb.me i { position:absolute; right:0; top:0; bottom:0; background:var(--brand); border-radius:99px; width:0; transition:width .7s var(--ease); }
  .ts-tb.op i { position:absolute; left:0; top:0; bottom:0; background:#E8E4DC; border-radius:99px; width:0; transition:width .7s var(--ease); }
  .ts-tp { text-align:center; font-size:10px; font-weight:800; letter-spacing:.06em; color:rgba(255,255,255,.6); }
  .ts-tcall { display:grid; grid-template-columns:1fr 44px 1fr; margin:-5px 0 8px; }
  .ts-tcall span { font-size:9px; font-weight:800; letter-spacing:.05em; }
  .ts-tcall .l { text-align:left; color:var(--brand); }
  .ts-tcall .r { text-align:right; color:#9BE7AE; }
  .ts-tcall .dim { color:rgba(255,255,255,.4); }
  .ts-firsts { text-align:center; font-size:11px; color:rgba(255,255,255,.55); margin:2px 0 14px; }
  .ts-firsts b { color:#fff; }
  .ts-ticket { background:var(--paper); color:#1D1D1F; border-radius:14px; padding:13px 15px; position:relative; text-align:left; }
  .ts-ticket::before { content:''; position:absolute; left:0; right:0; top:-6px; height:12px;
    background:radial-gradient(circle 6px at 8px 6px, #1D1D1F 0 5px, transparent 6px) 0 0/20px 12px repeat-x; }
  .ts-tk { font-family:var(--mono); font-size:9px; font-weight:700; letter-spacing:.18em; color:#86868B; margin-bottom:6px; }
  .ts-ticket p { font-size:13.5px; line-height:1.5; }
  .ts-ticket b { color:var(--brand-ink); }
  .ts-strip { display:flex; gap:8px; margin-top:12px; }
  .ts-chip2 { flex:1; min-width:0; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1); border-radius:12px; padding:8px; display:flex; align-items:center; gap:8px; text-align:left; }
  .ts-chip2 img { width:30px; height:30px; border-radius:50%; object-fit:cover; background:#333; flex:none; }
  .ts-chip2 .n { font-size:11px; font-weight:700; line-height:1.2; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ts-chip2 .k { font-size:8.5px; font-weight:800; letter-spacing:.06em; margin-top:2px; }
  .ts-chip2.get .k { color:#9BE7AE; }
  .ts-chip2.give .k { color:var(--brand); }
  .ts-tape-foot { display:flex; align-items:center; justify-content:space-between; margin-top:18px; }
  .ts-tape-opens { font-size:10px; font-weight:800; letter-spacing:.14em; color:rgba(255,255,255,.5); text-align:left; }
  .ts-tape-opens b { display:block; font-size:30px; color:var(--brand); letter-spacing:-.02em; animation:tstick .4s var(--ease); }
  @keyframes tstick { 0% { transform:scale(1.35); opacity:.4; } 100% { transform:scale(1); opacity:1; } }
  .ts-page .ts-btn-tape, .ts-scrim .ts-btn-tape { background:var(--brand); color:#fff; font-size:14px; font-weight:800; padding:13px 24px; border-radius:99px; border:none; cursor:pointer; font-family:inherit; box-shadow:0 6px 20px rgba(255,122,0,.4); transition:transform .15s var(--ease); }
  .ts-page .ts-btn-tape:hover, .ts-scrim .ts-btn-tape:hover { transform:translateY(-1px); }
  .ts-page .ts-btn-tape:active, .ts-scrim .ts-btn-tape:active { transform:scale(.96); }

    /* scouting reveal — opponent header */
  .ts-opp { display:flex; flex-direction:column; align-items:center; gap:10px; margin:6px 0 18px; }
  .ts-opp-ava { width:84px; height:84px; border-radius:50%; background:var(--ink); color:#fff; display:flex; align-items:center; justify-content:center; font-size:30px; font-weight:700; overflow:hidden; }
  .ts-opp-ava img { width:100%; height:100%; object-fit:cover; }
  .ts-opp-ava.taco { background:radial-gradient(circle at 50% 38%,#FFD9A8,#FFB55C); font-size:44px; }
  .ts-opp-name { font-size:20px; font-weight:700; letter-spacing:-.01em; color:var(--ink); }
  .ts-opp-line { display:flex; align-items:center; gap:8px; flex-wrap:wrap; justify-content:center; font-size:13.5px; color:var(--ink2); }
  .ts-win { display:inline-block; font-size:10px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; padding:3px 10px; border-radius:980px; }
  .ts-win.contender { background:var(--green-soft); color:var(--green-ink); }
  .ts-win.rebuilding { background:var(--brand-soft); color:var(--brand-ink); }
  .ts-win.bubble { background:var(--surface); color:var(--ink2); }
  .ts-opp-built { font-size:14px; color:var(--ink2); }
  .ts-opp-built b { color:var(--ink); font-weight:600; }
  .ts-how { font-size:13px; color:var(--ink3); margin:0 0 14px; }
  .ts-opens { display:flex; align-items:baseline; justify-content:center; gap:8px; margin-bottom:12px; }
  .ts-opens .lbl { font-size:10px; font-weight:700; letter-spacing:.1em; color:var(--ink3); text-transform:uppercase; }
  .ts-opens .count { font-size:34px; font-weight:800; letter-spacing:-.04em; color:var(--brand); line-height:1; }

  /* invite waiting — full link, unmissable copy */
  .ts-invite-link { display:block; background:var(--surface); border-radius:12px; padding:12px 14px; margin-bottom:12px; }
  .ts-invite-link code { display:block; font-family:var(--mono); font-size:13px; line-height:1.5; color:var(--ink); word-break:break-all; text-align:left; }
  .ts-invite-actions { display:flex; gap:8px; margin-bottom:16px; }
  .ts-page .ts-invite-copy { flex:1; background:var(--ink); color:#fff; border:none; border-radius:980px; padding:13px 18px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; white-space:nowrap; min-height:46px; }
  .ts-page .ts-invite-copy.copied { background:var(--green); }
  .ts-page .ts-invite-share { flex:1; background:#fff; border:1px solid var(--hair); color:var(--ink); border-radius:980px; padding:13px 18px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; min-height:46px; }

  @media (max-width:1080px) { .ts-content { grid-template-columns:1fr; } .ts-standcard { max-width:420px; } }
  @media (max-width:860px) { .ts-window { display:none; } .ts-hero-in { padding:34px 22px; } .ts-headline { font-size:44px; } .ts-content { padding:24px 22px 60px; } .ts-gate-form { grid-template-columns:1fr; } }
  @media (max-width:600px) {
    .ts-hero-in { padding:28px 16px; }
    .ts-headline { font-size:40px; }
    .ts-dek { font-size:16px; }
    .ts-cta-row { flex-direction:column; align-items:stretch; }
    .ts-btn-step, .ts-btn-taco, .ts-btn-invite { width:100%; justify-content:center; min-height:48px; }
    .ts-seg { width:100%; } .ts-seg button { flex:1; min-height:40px; }
    .ts-chips { gap:6px; }
    .ts-content { padding:20px 16px 56px; }
    .ts-punch { padding:18px 16px; }
    .ts-stamps { gap:8px; } .ts-stamp { width:38px; height:38px; font-size:18px; }
    .ts-standcard { max-width:none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ts-page *, .ts-page *::before, .ts-page *::after,
    .ts-scrim *, .ts-scrim *::before, .ts-scrim *::after {
      animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important;
    }
  }
`;

const initials = (name) => (name || '?').slice(0, 2).toUpperCase();

function timeSince(d) {
  if (!d) return 'just sat down';
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return 'just sat down';
  return `${Math.floor(secs / 60)}m at the window`;
}

// Pre-match reveal: the Tale of the Tape — two corners, mirrored bars, the angle.
function fmtRec(tc) {
  const rec = tc?.prevRecord ? `${tc.prevRecord.wins}-${tc.prevRecord.losses} last yr` : 'new team';
  const fin = tc?.prevFinishLabel && tc.prevFinishLabel !== 'Missed playoffs' ? ` · ${tc.prevFinishLabel.toLowerCase()}` : '';
  return rec + fin;
}

function TapeCorner({ side, name, avatar, isBot, tc }) {
  return (
    <div className={`ts-tc ${side}`}>
      <div className={`ts-tc-ava${isBot ? ' taco' : ''}`}>
        {isBot ? '🌮' : (avatar
          ? <img src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
          : initials(name))}
      </div>
      <div className="ts-tc-name">{name}</div>
      <div className="ts-tc-win">{tc?.windowLabel || '—'}</div>
      <div className="ts-tc-fact">{tc?.valueRank && tc?.numTeams ? `#${tc.valueRank} of ${tc.numTeams} talent` : ''}<br />{fmtRec(tc)}</div>
    </div>
  );
}

function TapeReport({ foundData, myName, oppName }) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(t);
  }, []);
  const myTc = foundData.myRosterData?.teamContext;
  const oppTc = foundData.opponentRosterData?.teamContext;
  const scout = buildScout({
    myPlayers: foundData.myRosterData?.players,
    oppPlayers: foundData.opponentRosterData?.players,
    myPicks: foundData.myRosterData?.picks,
    oppPicks: foundData.opponentRosterData?.picks,
    oppTc,
  });
  const callout = (r) => {
    if (r.tag === 'you') return { l: `YOUR SURPLUS · ${fmtK(r.mine)}`, r: fmtK(r.theirs), lDim: false, rDim: true };
    if (r.tag === 'them') return { l: fmtK(r.mine), r: `${fmtK(r.theirs)} · THEIR SURPLUS`, lDim: true, rDim: false };
    if (r.mine >= r.theirs) return { l: `${fmtK(r.mine)} · EDGE YOU`, r: fmtK(r.theirs), lDim: false, rDim: true };
    return { l: fmtK(r.mine), r: `${fmtK(r.theirs)} · EDGE THEM`, lDim: true, rDim: false };
  };
  const lead = scout.angles[0] || '';
  return (
    <>
      <div className="ts-tape-eyebrow">TALE OF THE TAPE</div>
      <div className="ts-tape-head">
        <TapeCorner side="me" name={myName} avatar={foundData.myAvatar} tc={myTc} />
        <div className="ts-tape-vs">VS</div>
        <TapeCorner side="op" name={oppName} avatar={foundData.opponentAvatar} isBot={foundData.opponentIsBot} tc={oppTc} />
      </div>
      <div className="ts-tape">
        {scout.rows.map((r, idx) => {
          const c = callout(r);
          return (
            <React.Fragment key={r.pos}>
              <div className="ts-tr">
                <span className="ts-tb me"><i style={{ width: revealed ? `${(r.mine / r.max) * 100}%` : '0%', transitionDelay: `${idx * 55}ms` }} /></span>
                <span className="ts-tp">{r.pos}</span>
                <span className="ts-tb op"><i style={{ width: revealed ? `${(r.theirs / r.max) * 100}%` : '0%', transitionDelay: `${idx * 55}ms` }} /></span>
              </div>
              <div className="ts-tcall">
                <span className={`l${c.lDim ? ' dim' : ''}`}>{c.l}</span><span />
                <span className={`r${c.rDim ? ' dim' : ''}`}>{c.r}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="ts-firsts">1st-round picks — you <b>{scout.myFirsts}</b> · them <b>{scout.theirFirsts}</b></div>
      <div className="ts-ticket">
        <div className="ts-tk">THE ANGLE</div>
        <p><b>{lead}</b> {scout.read}</p>
      </div>
      {(scout.targets.length > 0 || scout.dangles.length > 0) && (
        <div className="ts-strip">
          {scout.targets.map((t) => (
            <div className="ts-chip2 get" key={t.sleeperId}><img src={`https://sleepercdn.com/content/nfl/players/thumb/${t.sleeperId}.jpg`} alt="" onError={(e) => { e.target.style.visibility = 'hidden'; }} /><div style={{ minWidth: 0 }}><div className="n">{t.name}</div><div className="k">HUNT</div></div></div>
          ))}
          {scout.dangles.slice(0, 1).map((t) => (
            <div className="ts-chip2 give" key={t.sleeperId}><img src={`https://sleepercdn.com/content/nfl/players/thumb/${t.sleeperId}.jpg`} alt="" onError={(e) => { e.target.style.visibility = 'hidden'; }} /><div style={{ minWidth: 0 }}><div className="n">{t.name}</div><div className="k">DANGLE</div></div></div>
          ))}
        </div>
      )}
    </>
  );
}

export default function TradeArenaQueue() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { leagues, sleeperUser } = useShell();
  const autoBotFired = useRef(false);

  const [rosterType, setRosterType] = useState('own');
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [phase, setPhase] = useState('lobby'); // lobby | searching | found | invite_waiting
  const [botMode, setBotMode] = useState(false); // true while matching/​playing the Taco
  const [foundData, setFoundData] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [inviteCode, setInviteCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const [stats, setStats] = useState(null);
  const [liveGames, setLiveGames] = useState([]);

  const [email, setEmail] = useState('');
  const [sleeperInput, setSleeperInput] = useState('');
  const [emailState, setEmailState] = useState('idle');

  const socketRef = useRef(null);
  const dynastyLeagues = (leagues || []).filter((l) => l.leagueId);

  // ── data ──
  useEffect(() => {
    if (!user) return;
    fetch('/api/arena/stats', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.stats) setStats(d.stats); })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetch('/api/arena/live')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.games) setLiveGames(d.games); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (dynastyLeagues.length > 0 && !selectedLeagueId) setSelectedLeagueId(dynastyLeagues[0].leagueId || '');
  }, [dynastyLeagues.length]); // eslint-disable-line

  // ── socket ──
  useEffect(() => {
    if (!user) return;
    const sock = getArenaSocket(user.id, user.sleeperUsername, sleeperUser?.user_id);
    socketRef.current = sock;

    const onMatch = (data) => {
      setFoundData(data);
      const skew = data.serverNow ? new Date(data.serverNow).getTime() - Date.now() : 0;
      const startsAt = data.startsAt ? new Date(data.startsAt).getTime() - skew : Date.now() + 6000;
      setCountdown(Math.max(1, Math.ceil((startsAt - Date.now()) / 1000)));
      setPhase('found');
    };
    const onQueue = ({ status }) => { if (status === 'left') setPhase('lobby'); };
    const onInvite = ({ code }) => { setInviteCode(code); setPhase('invite_waiting'); };
    const onInvalid = () => { setPhase('lobby'); setInviteCode(null); };
    const onErr = () => setPhase('lobby');

    sock.on('arena:match_found', onMatch);
    sock.on('arena:queue_status', onQueue);
    sock.on('arena:invite_created', onInvite);
    sock.on('arena:invite_invalid', onInvalid);
    sock.on('arena:error', onErr);

    // "Run it back" from the result screen — straight back into a Taco game.
    if (location.state?.autoBot && !autoBotFired.current) {
      autoBotFired.current = true;
      window.history.replaceState({}, ''); // don't re-fire on back-nav
      setBotMode(true);
      setPhase('searching');
      sock.emit('arena:play_bot', rosterPayload());
    }

    return () => {
      sock.off('arena:match_found', onMatch);
      sock.off('arena:queue_status', onQueue);
      sock.off('arena:invite_created', onInvite);
      sock.off('arena:invite_invalid', onInvalid);
      sock.off('arena:error', onErr);
    };
  }, [user, sleeperUser]); // eslint-disable-line react-hooks/exhaustive-deps -- autoBot fires once via ref guard

  // ── found → countdown → room ──
  useEffect(() => {
    if (phase !== 'found' || !foundData) return;
    if (countdown <= 0) {
      navigate(`/taco-stand/room/${foundData.sessionId}`, { state: foundData });
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 900);
    return () => clearTimeout(t);
  }, [phase, countdown, foundData, navigate]);

  const rosterPayload = useCallback(() => {
    const league = dynastyLeagues.find((l) => l.leagueId === selectedLeagueId);
    return {
      rosterMode: rosterType,
      leagueId: rosterType === 'own' ? selectedLeagueId || null : null,
      leagueName: rosterType === 'own' ? league?.name || null : null,
    };
  }, [rosterType, selectedLeagueId, dynastyLeagues]);

  const handleStepUp = () => { setBotMode(false); socketRef.current?.emit('arena:join_queue', rosterPayload()); setPhase('searching'); };
  const handlePlayBot = () => { setBotMode(true); socketRef.current?.emit('arena:play_bot', rosterPayload()); setPhase('searching'); };
  const handleLeave = () => { socketRef.current?.emit('arena:leave_queue'); setBotMode(false); setPhase('lobby'); };
  const handleInvite = () => { socketRef.current?.emit('arena:create_invite', rosterPayload()); };
  const handleCancelInvite = () => { if (inviteCode) socketRef.current?.emit('arena:cancel_invite', { code: inviteCode }); setInviteCode(null); setPhase('lobby'); };

  const inviteUrl = inviteCode ? `${window.location.origin}/taco-stand/invite/${inviteCode}` : '';
  const handleCopy = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(inviteUrl).then(done).catch(done); return; }
    // Fallback for browsers without the async clipboard API
    const ta = document.createElement('textarea');
    ta.value = inviteUrl; document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (_) { /* best effort */ }
    document.body.removeChild(ta); done();
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailState('sending');
    try {
      const res = await fetch('/api/auth/request-magic-link', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), returnUrl: '/taco-stand', sleeperUsername: sleeperInput.trim() || undefined }),
      });
      setEmailState(res.ok ? 'sent' : 'error');
    } catch { setEmailState('error'); }
  };

  // ── progression display ──
  const streak = stats?.currentStreak ?? 0;
  const deals = stats?.dealsCount ?? 0;
  const cardSize = stats?.punchcardSize ?? 8;
  const filled = stats?.punchcardFilled ?? 0;
  const toUnlock = stats?.dealsToUnlock ?? cardSize;
  const ratingUnlocked = stats?.ratingUnlocked;
  const myName = user?.sleeperUsername || user?.email?.split('@')[0] || 'you';

  const renderStamps = () => {
    const out = [];
    for (let i = 0; i < cardSize - 1; i++) {
      const cls = ['ts-stamp', i < filled ? 'done' : '', i === filled - 1 ? 'latest' : ''].filter(Boolean).join(' ');
      out.push(<div key={i} className={cls}>🌮</div>);
    }
    out.push(<div key="goal" className="ts-stamp goal">★</div>);
    return out;
  };

  return (
    <>
      <style>{styles}</style>
      <div className="ts-page">

        <div className="ts-awning" />
        {/* ── Hero ── */}
        <section className="ts-hero">
          <div className="ts-hero-in">
            <div className="ts-hero-left">
              <div className="ts-eyebrow"><span className="l" /> The Taco Stand</div>
              <h1 className="ts-headline">What'll it<br />be<span className="q">?</span></h1>
              <p className="ts-dek">Live 1v1 trading with real rosters. Scout them, then two minutes to stack every deal you can. Nothing touches your real league.</p>

              {user ? (
                <div className="ts-setup">
                  <div className="ts-seg">
                    <button className={rosterType === 'own' ? 'on' : ''} onClick={() => setRosterType('own')}>Your roster</button>
                    <button className={rosterType === 'random' ? 'on' : ''} onClick={() => setRosterType('random')}>Surprise me</button>
                  </div>
                  {rosterType === 'own' && dynastyLeagues.length > 0 && (
                    <div className="ts-chips">
                      {dynastyLeagues.slice(0, 6).map((l) => (
                        <button key={l.sleeperLeagueId} className={`ts-chip${selectedLeagueId === l.leagueId ? ' on' : ''}`} onClick={() => setSelectedLeagueId(l.leagueId)}>
                          {l.name || l.sleeperLeagueId}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="ts-cta-row">
                    <button className="ts-btn-step" onClick={handleStepUp}>🌮 Step up to the window <span>→</span></button>
                    <button className="ts-btn-taco" onClick={handlePlayBot}>🌮 Play the Taco</button>
                    <button className="ts-btn-invite" onClick={handleInvite}>＋ Invite a friend</button>
                  </div>
                  <span className="ts-cta-note">Nobody at the window? Play the Taco — the machine never sleeps.</span>
                </div>
              ) : (
                <div className="ts-gate">
                  {emailState === 'sent' ? (
                    <div className="ts-gate-sent">Link sent. Check your email — it'll bring you right back to the window.</div>
                  ) : (
                    <form className="ts-gate-form" onSubmit={handleMagicLink}>
                      <input className="ts-gate-in" placeholder="Sleeper username" value={sleeperInput} onChange={(e) => setSleeperInput(e.target.value)} disabled={emailState === 'sending'} />
                      <input className="ts-gate-in" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={emailState === 'sending'} />
                      <button className="ts-gate-sub" type="submit" disabled={emailState === 'sending' || !email.trim()}>{emailState === 'sending' ? 'Sending…' : 'Step up →'}</button>
                    </form>
                  )}
                  {emailState === 'error' && <div className="ts-gate-err">Something went wrong. Try again.</div>}
                </div>
              )}
            </div>

            {/* window */}
            <div className="ts-window">
              <div className="ts-window-frame">
                <div className="ts-window-top" />
                <div className="ts-window-body">
                  <div className="ts-host"><div className="steam"><i /><i /><i /></div>🌮</div>
                  <div className="ts-host-says">"Step right up."</div>
                  <div className="ts-host-sub">{liveGames.length > 0 ? `${liveGames.length} cooking right now` : 'The window is open'}</div>
                </div>
                <div className="ts-window-foot">
                  <span className="ts-wf-num">NOW SERVING <b>#{String(420 + deals).padStart(4, '0')}</b></span>
                  <span className="ts-open green"><span className="pip" /> Open</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Content ── */}
        <div className="ts-content">
          <div>
            {/* punch card — only for signed-in players */}
            {user && (
              <section className="ts-section">
                <div className="ts-sechead"><span className="ts-k">Your punch card</span></div>
                <div className="ts-punch">
                  <div className="ts-punch-top">
                    <div>
                      <div className="ts-punch-title">Regulars eat free</div>
                      <div className="ts-punch-sub">Close deals to fill your card. Fill it, and your Stand Rating unlocks.</div>
                    </div>
                    <div className="ts-streak">
                      <div className="ts-streak-n"><span className="flame">🔥</span>{streak}</div>
                      <div className="ts-streak-l">deal streak</div>
                    </div>
                  </div>
                  <div className="ts-stamps">{renderStamps()}</div>
                  <div className="ts-punch-foot">
                    {ratingUnlocked
                      ? <>Your Stand Rating is live — keep the streak alive for the leaderboard.</>
                      : <>{toUnlock} more {toUnlock === 1 ? 'deal' : 'deals'} till <b>Repeat Customer</b> <span className="ts-lock">🔒 Stand Rating unlocks at ★</span></>}
                  </div>
                </div>
              </section>
            )}

            {/* live tickets */}
            <section className="ts-section">
              <div className="ts-sechead">
                <span className="ts-open"><span className="pip" /> At the window now</span>
                {liveGames.length > 0 && <span className="ts-k" style={{ marginLeft: 'auto' }}>{liveGames.length} cooking</span>}
              </div>
              {liveGames.length === 0 ? (
                <div className="ts-empty">Nobody's at the window yet. Be the first — step up.</div>
              ) : (
                <div className="ts-rail-tk">
                  {liveGames.map((g) => (
                    <div key={g.sessionId} className="ts-lt">
                      <div className="ts-lt-head"><span className="ts-lt-num">ORDER #{String(g.sessionId).slice(-4).toUpperCase()}</span><span className="ts-lt-live"><span className="pip" /> cooking</span></div>
                      <div className="ts-lt-body">
                        <div className="ts-lt-players">
                          <div className="ts-lt-avs"><div className="ts-lt-av">{initials(g.player1Username)}</div><div className="ts-lt-av">{initials(g.player2Username)}</div></div>
                          <div className="ts-lt-vs"><b>{g.player1Username || 'someone'}</b> vs <b>{g.player2Username || 'someone'}</b></div>
                        </div>
                      </div>
                      <div className="ts-lt-foot"><span>{timeSince(g.startedAt)}</span><span>🌮 haggling</span></div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* stand card */}
          {user && (
            <div className="ts-standcard">
              <div className="awn" />
              <div className="ts-sc-name">{myName}</div>
              <div className="ts-sc-handle">@{user?.sleeperUsername || myName} · {stats?.gamesPlayed ? 'regular' : 'new here'}</div>
              <div className="ts-sc-stat"><span>Deal streak</span><b><span className="flame">🔥</span> {streak}</b></div>
              <div className="ts-sc-stat"><span>Deals cooked</span><b>{deals}</b></div>
              <div className="ts-sc-stat"><span>Stand Rating</span><b style={ratingUnlocked ? undefined : { color: 'rgba(255,255,255,.5)', fontWeight: 500 }}>{ratingUnlocked ? `${stats.elo}${stats.rank ? ` · #${stats.rank}` : ''}` : '🔒 locked'}</b></div>
              <div style={{ marginTop: 16 }}>
                <div className="ts-k" style={{ color: 'rgba(255,255,255,.55)', marginBottom: 10 }}>Badges</div>
                <div className="ts-badges">
                  {(stats?.badges || []).map((b) => (
                    <span key={b.key} className={`ts-bdg${b.earned ? '' : ' locked'}`}>{b.earned ? b.emoji : '🔒'} {b.label}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Matchmaking: searching ── */}
      {phase === 'searching' && (
        <div className="ts-scrim">
          <div className="ts-mm">
            <div className="ts-mm-awn" />
            <div className="ts-mm-body">
              <div className="ts-mm-host"><div className="steam"><i /><i /><i /></div>🌮</div>
              <div className="ts-mm-title">{botMode ? 'The Taco is stepping up…' : 'Finding you a customer…'}</div>
              <div className="ts-mm-sub">{botMode ? 'Grabbing a roster and reading the table.' : "The Taco's flagging someone down at the window."}</div>
              <div className="ts-mm-dots"><i /><i /><i /></div>
              <div className="ts-mm-meta">{rosterType === 'own' ? (dynastyLeagues.find((l) => l.leagueId === selectedLeagueId)?.name || 'Your roster') : 'Surprise roster'}</div>
              <button className="ts-btn-leave" onClick={handleLeave}>Leave the line</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Matchmaking: the Tale of the Tape ── */}
      {phase === 'found' && foundData && (
        <div className="ts-scrim">
          <div className="ts-mm dark">
            <div className="ts-mm-awn" />
            <div className="ts-mm-body">
              <TapeReport
                foundData={foundData}
                myName={myName}
                oppName={foundData.opponentIsBot ? 'The Taco' : (foundData.opponentSleeperUsername || 'Your opponent')}
              />
              <div className="ts-tape-foot">
                <div className="ts-tape-opens">STARTS IN<b key={countdown}>{countdown > 0 ? countdown : '·'}</b></div>
                <button className="ts-btn-tape" onClick={() => navigate(`/taco-stand/room/${foundData.sessionId}`, { state: foundData })}>Jump in & scout →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite a friend: waiting ── */}
      {phase === 'invite_waiting' && (
        <div className="ts-scrim">
          <div className="ts-mm">
            <div className="ts-mm-awn" />
            <div className="ts-mm-body">
              <div className="ts-mm-host"><div className="steam"><i /><i /><i /></div>🌮</div>
              <div className="ts-mm-title">Hold a table for a friend</div>
              <div className="ts-mm-sub">Send them this link. When they sit down, the kitchen opens.</div>
              <div className="ts-invite-link"><code>{inviteUrl}</code></div>
              <div className="ts-invite-actions">
                <button className={`ts-invite-copy${copied ? ' copied' : ''}`} onClick={handleCopy}>{copied ? '✓ Copied' : 'Copy link'}</button>
                {typeof navigator !== 'undefined' && navigator.share ? (
                  <button className="ts-invite-share" onClick={() => navigator.share({ title: 'The Taco Stand', text: 'Trade me. Live, 1v1, two minutes.', url: inviteUrl }).catch(() => {})}>Share…</button>
                ) : null}
              </div>
              <div className="ts-mm-dots"><i /><i /><i /></div>
              <div className="ts-mm-meta">Waiting for your friend to step up…</div>
              <button className="ts-btn-leave" onClick={handleCancelInvite}>Cancel the table</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
